import { Injectable, Signal, computed, inject } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { Store } from '@ngrx/store';

import * as AuthSelectors from '../auth/store/auth.selectors';
import { PermissionService } from '../shared/services/permission.service';
import { environment } from '../../environments/environment';
import { RAIL_WORLDS, RailWorld } from './nav-data.constant';
import { NavViewer, visibleRailWorlds } from './nav-visibility.util';

/**
 * The nav tree as THIS viewer sees it (NBO-2331 rail, NBO-2388 top bar).
 *
 * One computed, two consumers: the rail renders `visibleWorlds()`, and the top
 * bar resolves its section label from the same signal — so the string in the
 * top bar is, by construction, the string the rail shows for that destination
 * for every viewer. That includes a role-collapsed section: a sales-only user
 * sees Sales reduced to Action Hub alone, which the rail renders as a single
 * link row labeled "Sales" — and the top bar says "Sales" too, because it
 * reads this tree, not the structural RAIL_WORLDS. Keeping the filter in the
 * rail component would have left the top bar with only the structural tree
 * (and "Action Hub" for that viewer).
 *
 * Sections keep only their visible items, worlds only their non-empty
 * sections. Before the role claim resolves the tree is EMPTY (every gated
 * section drops) — consumers gate on `ChromeReadyService.navReady` rather
 * than reading a half-answer.
 */
@Injectable({ providedIn: 'root' })
export class RailTreeService {
  private readonly store = inject(Store);
  private readonly permissionService = inject(PermissionService);

  // Read so visibleWorlds() recomputes when the role set lands/changes;
  // the actual checks go through PermissionService (same store state).
  private readonly roles = toSignal(this.store.select(AuthSelectors.selectAuthRoles), { initialValue: [] });

  // The rules live in nav-visibility.util, shared with the back-office user's menu-access preview
  // (NBO-2547), so the preview can never disagree with the rail.
  private readonly viewer: NavViewer = {
    hasAnyRole: (role) => this.permissionService.hasAnyRole(role),
    isPlatformStaff: () => this.permissionService.isPlatformStaff(),
    environment: environment.environment
  };

  readonly visibleWorlds: Signal<RailWorld[]> = computed(() => {
    this.roles();
    return visibleRailWorlds(RAIL_WORLDS, this.viewer);
  });
}
